import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Award, CheckCheck, CreditCard, Flame, Lock, PiggyBank, Sparkles, Tags, Target, Trophy, Wallet } from "lucide-react";
import { CoinMascot } from "@/components/brand/CoinMascot";
import { useHousehold } from "@/context/HouseholdContext";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const BADGE_ICONS = { first_check: CheckCheck, streak: Flame, budget_held: Wallet, zero_todo: Tags, saver: PiggyBank, goal_reached: Trophy, cards: CreditCard };
const MESSAGES = {
  celebrate: "Incroyable, vos finances sont au top !",
  happy: "Beau travail, encore un petit effort !",
  idle: "On avance bien : validez et pointez vos opérations.",
  worried: "Quelques points à régler : je vous aide ?",
};
const R = 52;
const C = 2 * Math.PI * R;

const Ring = ({ score }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf, t0;
    const step = (t) => { t0 = t0 || t; const p = Math.min((t - t0) / 1200, 1); setN(Math.round(score * (1 - (1 - p) ** 3))); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [score]);
  const color = score >= 80 ? "#D8BD78" : score >= 60 ? "#87B8A5" : score >= 40 ? "#9DB4D8" : "#E3A79A";
  return (
    <div className="relative h-28 w-28 shrink-0 sm:h-36 sm:w-36" data-testid="health-ring">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r={R} fill="none" stroke="currentColor" strokeWidth="10" className="text-secondary" />
        <motion.circle cx="60" cy="60" r={R} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" strokeDasharray={C}
          initial={{ strokeDashoffset: C }} animate={{ strokeDashoffset: C * (1 - score / 100) }} transition={{ duration: 1.2, ease: "easeOut" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="num text-4xl font-extrabold leading-none" data-testid="health-score">{n}</span>
        <span className="text-[11px] text-muted-foreground">/ 100</span>
      </div>
    </div>
  );
};

const Badges = ({ list }) => (
  <div className="flex flex-wrap gap-2" data-testid="health-badges">
    {list.map((b, i) => {
      const Icon = BADGE_ICONS[b.key] || Award;
      return (
        <motion.span key={b.key} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.3 + i * 0.07, type: "spring" }}
          title={b.label + (b.progress && !b.earned ? ` (${b.progress})` : "")} data-testid={`badge-${b.key}`}
          className={cn("flex h-10 w-10 items-center justify-center rounded-full ring-2 transition-transform hover:scale-110", b.earned ? "bg-gradient-to-br from-[#F3DFA2] to-[#D8BD78] text-[#2B2210] ring-[#D8BD78]/40 shadow" : "bg-secondary text-muted-foreground/50 ring-transparent")}>
          {b.earned ? <Icon className="h-4 w-4" /> : <Lock className="h-3.5 w-3.5" />}
        </motion.span>
      );
    })}
  </div>
);

const Challenge = ({ c }) => (
  <li className="text-sm" data-testid={`challenge-${c.key}`}>
    <div className="flex items-center gap-2">
      <Target className={cn("h-3.5 w-3.5 shrink-0", c.done ? "text-success" : "text-muted-foreground")} />
      <span className={cn("min-w-0 flex-1 truncate", c.done && "line-through opacity-60")}>{c.label}</span>
      <span className="num shrink-0 text-xs font-semibold">{c.value}/{c.target}</span>
    </div>
    <div className="ml-5 mt-1 h-1.5 overflow-hidden rounded-full bg-secondary">
      <motion.div className={cn("h-full rounded-full", c.done ? "bg-success" : "bg-[#D8BD78]")} initial={{ width: 0 }} animate={{ width: `${Math.min(100, (c.value / c.target) * 100)}%` }} transition={{ duration: 0.9, delay: 0.4 }} />
    </div>
  </li>
);

export const HealthHero = () => {
  const { active } = useHousehold();
  const { data: d } = useQuery({ queryKey: ["health", active?.id], queryFn: () => api.get(`/finance/health?hid=${active.id}`).then((r) => r.data), enabled: !!active });
  const [details, setDetails] = useState(false);
  if (!d) return <div className="h-56 animate-pulse rounded-3xl bg-muted" />;
  return (
    <section className="card-surface tone-wash tone-butter relative overflow-hidden p-5 sm:p-6" data-testid="health-hero">
      <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#D8BD78]/15" />
      <div className="relative grid gap-6 lg:grid-cols-[auto_1fr_1fr]">
        <div className="flex items-center gap-4">
          <Ring score={d.score} />
          <div className="min-w-0">
            <div className="eyebrow">Santé financière</div>
            <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-lg font-extrabold sm:text-xl" data-testid="health-level"><Sparkles className="h-4 w-4 text-[#D8BD78]" /><span className="whitespace-nowrap">Niveau {d.level.rank}</span><span className="whitespace-nowrap">· {d.level.name}</span></div>
            {d.level.next_at && <p className="text-xs text-muted-foreground">Encore {d.level.next_at - d.score} pts pour « {d.level.next_name} »</p>}
            <div className="mt-3 flex items-end gap-2">
              <CoinMascot mood={d.mood === "celebrate" ? "celebrate" : "idle"} size={52} />
              <p className="relative mb-3 max-w-[13rem] rounded-2xl rounded-bl-sm bg-card px-3 py-2 text-xs shadow-sm" data-testid="health-mascot-message">{MESSAGES[d.mood]}</p>
            </div>
          </div>
        </div>
        <button type="button" onClick={() => setDetails(!details)} className="text-left text-xs font-semibold text-primary lg:hidden" data-testid="health-details-toggle">{details ? "Masquer le détail du score" : "Voir le détail du score"}</button>
        <div className={cn("space-y-2 lg:block", !details && "hidden")} data-testid="health-parts">
          {d.parts.map((p) => (
            <div key={p.key} className="text-sm" data-testid={`health-part-${p.key}`}>
              <div className="flex items-baseline justify-between gap-2"><span className="font-medium">{p.label}</span><span className="num text-xs font-semibold">{p.points}/20</span></div>
              <div className="h-1.5 overflow-hidden rounded-full bg-secondary"><motion.div className="h-full rounded-full bg-[#87B8A5]" initial={{ width: 0 }} animate={{ width: `${p.points * 5}%` }} transition={{ duration: 0.9 }} /></div>
              <div className="text-[11px] text-muted-foreground">{p.hint}</div>
            </div>
          ))}
        </div>
        <div className="space-y-4">
          <div><div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground"><span>Badges</span><span data-testid="health-badges-count">{d.earned_count}/{d.badges.length}</span></div><Badges list={d.badges} /></div>
          <div><div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Défis du mois</div><ul className="space-y-2.5">{d.challenges.map((c) => <Challenge key={c.key} c={c} />)}</ul></div>
        </div>
      </div>
    </section>
  );
};
